import { loadQuery } from "react-relay";
import {
	Environment,
	FetchFunction,
	fetchQuery,
	GraphQLTaggedNode,
	Network,
	Observable,
	OperationType,
	RecordSource,
	RelayFeatureFlags,
} from "relay-runtime";
import RelayModernStore from "relay-runtime/lib/store/RelayModernStore";
import { createMockEnvironment, MockEnvironment } from "relay-test-utils";
import { mockOperation, mockRawOperation, setOperationData } from "./relay-mock";
import { originForBackend } from "./utils/appOrigin";
import { mutationResponseError } from "./utils/graphqlError";

RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;

const DEFAULT_GRAPHQL_TIMEOUT_MS = 10_000;
const graphqlTimeoutMsFromEnv = Number(import.meta.env.VITE_GRAPHQL_TIMEOUT_MS);
const GRAPHQL_TIMEOUT_MS =
	Number.isFinite(graphqlTimeoutMsFromEnv) && graphqlTimeoutMsFromEnv > 0 ? graphqlTimeoutMsFromEnv : DEFAULT_GRAPHQL_TIMEOUT_MS;
export const API_URL = originForBackend("api", import.meta.env.VITE_API_URL);
const isMocked = !API_URL || import.meta.env.STORYBOOK === "true";

const handleAuthError = (errors: any[]) => {
	if (Array.isArray(errors)) {
		const hasAuthError = errors.some((error) => error?.extensions?.code === "AUTH_ERROR");

		if (hasAuthError) {
			localStorage.removeItem("better-auth-jwt");
			return true;
		}
	}
	return false;
};

const getData = async (body: string, retryCount = 0) => {
	if (!API_URL) {
		throw new Error("VITE_API_URL is not set");
	}
	const token = localStorage.getItem("better-auth-jwt");
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), GRAPHQL_TIMEOUT_MS);

	try {
		const res = await fetch(API_URL, {
			method: "POST",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body,
			signal: controller.signal,
		});

		if (!res.ok) {
			if (res.status === 401 && retryCount < 3) {
				const errorData = await res.json();

				if (errorData.errors && handleAuthError(errorData.errors)) {
					return getData(body, retryCount + 1);
				}
				await new Promise((resolve) => setTimeout(resolve, 500));
				return getData(body, retryCount + 1);
			}
			console.log({ res });
		}

		const data = await res.json();
		return data;
	} catch (error) {
		if (error instanceof Error && error.name === "AbortError") {
			throw new Error(`GraphQL request timed out after ${GRAPHQL_TIMEOUT_MS}ms`);
		}

		throw error;
	} finally {
		clearTimeout(timeout);
	}
};

export async function fetchGraphQL<TData>(query: string, variables: Record<string, unknown>): Promise<TData> {
	const response = isMocked ? mockRawOperation(query, variables) : await getData(JSON.stringify({ query, variables }));
	if (response.errors?.length) throw Object.assign(new Error(response.errors[0].message ?? "GraphQL request failed"), response);
	return response.data;
}

const fetchFn: FetchFunction = (operation, variables, cacheConfig, uploadables) => {
	return Observable.create((sink) => {
		let cancelled = false;

		const run = async () => {
			try {
				const data = await getData(
					JSON.stringify({
						query: operation.text, // GraphQL text from input
						variables,
					})
				);

				if (cancelled) {
					return;
				}

				if (operation.operationKind === "mutation" && data.errors) {
					// An Error whose message is the backend's, with `errors` and `data` attached.
					sink.error(mutationResponseError(data));
					return;
				}

				sink.next(data);
				sink.complete();
			} catch (error) {
				if (cancelled) {
					return;
				}

				if (error instanceof Error) {
					sink.error(error);
					return;
				}

				const networkError = new Error("GraphQL request failed");
				if (error && typeof error === "object") Object.assign(networkError, error);
				sink.error(networkError);
			}
		};

		void run();

		return () => {
			cancelled = true;
		};
	});
};

const network = Network.create(fetchFn);

let environment: Environment | MockEnvironment;
if (!isMocked) {
	environment = new Environment({
		network,
		store: new RelayModernStore(new RecordSource()),
		relayFieldLogger: (event) => {
			if (event.kind === "relay_resolver.error") {
				// Log this somewhere!
				console.warn(`Resolver error encountered in ${event.owner}.${event.fieldPath}`);
				console.warn(event.error);
			}
		},
	});
} else {
	console.info("No VITE_API_URL set, mocking relay");
	(window as any).global = null;
	environment = createMockEnvironment({});
	// Configure mock resolver globally for all queries
	(environment as MockEnvironment).mock.queueOperationResolver((operation) => {
		return mockOperation(operation);
	});
}

const isMockEnvironment = (environment: any): environment is MockEnvironment => {
	return environment.configName === "RelayModernMockEnvironment";
};

export const preloadQuery = async <TQuery extends OperationType>(
	...params: Parameters<typeof loadQuery<TQuery>>
): Promise<ReturnType<typeof loadQuery<TQuery>>> => {
	const [environment, preloadableRequest, variables, ...rest] = params;
	if (isMockEnvironment(environment)) {
		environment.mock.queueOperationResolver(mockOperation);
		environment.mock.queuePendingOperation(preloadableRequest as GraphQLTaggedNode, variables);
	}
	const loadedQuery = loadQuery(environment, preloadableRequest, variables, ...rest);

	if (!isMockEnvironment(environment)) {
		await fetchQuery(environment, preloadableRequest as GraphQLTaggedNode, variables, { fetchPolicy: "network-only" }).toPromise();
	}

	return loadedQuery;
};

export { environment, mockOperation, setOperationData };
